import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { FiMapPin, FiUser, FiCalendar, FiUsers, FiFilter, FiCheck, FiPlus } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import { matchAPI, userAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';
import logger from '../utils/logger';

const Search = () => {
  const navigate = useNavigate();
  const locationHook = useLocation();
  const { user: currentUser, login, token } = useAuthContext();
  
  const searchParams = new URLSearchParams(locationHook.search);
  const query = searchParams.get('q') || '';
  
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'matches', 'players'
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [followingMap, setFollowingMap] = useState({});

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  useEffect(() => {
    if (currentUser && players.length > 0) {
      const map = {};
      const followingList = currentUser.following || [];
      players.forEach(p => {
        // Handle both string IDs and object IDs if populated
        map[p._id] = followingList.some(f => 
          (typeof f === 'string' ? f : f._id) === p._id
        );
      });
      setFollowingMap(map);
    }
  }, [players, currentUser]);

  const performSearch = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [matchRes, playerRes] = await Promise.all([
        matchAPI.getAllMatches({ search: query }),
        userAPI.getAllUsers({ search: query })
      ]);

      if (matchRes.success) setMatches(matchRes.matches);
      if (playerRes.success) setPlayers(playerRes.users);
      
    } catch (err) {
      setError('Search failed. Please try again.');
      logger.error('Search error', { query, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleFollowToggle = async (targetId) => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const isFollowing = !!followingMap[targetId];
    const originalFollowing = [...(currentUser.following || [])];
    
    // 1. Optimistic UI update
    setFollowingMap(prev => ({ ...prev, [targetId]: !isFollowing }));

    try {
      if (isFollowing) {
        await userAPI.unfollowUser(targetId);
        // 2. Update global context
        const updatedFollowing = originalFollowing.filter(f => 
          (typeof f === 'string' ? f : f._id) !== targetId
        );
        const updatedUser = { ...currentUser, following: updatedFollowing };
        login(updatedUser, token);
        logger.info('User unfollowed successfully', { targetId });
      } else {
        await userAPI.followUser(targetId);
        // 2. Update global context
        const updatedFollowing = [...originalFollowing, targetId];
        const updatedUser = { ...currentUser, following: updatedFollowing };
        login(updatedUser, token);
        logger.info('User followed successfully', { targetId });
      }
    } catch (err) {
      // Revert on error
      setFollowingMap(prev => ({ ...prev, [targetId]: isFollowing }));
      logger.error('Follow toggle failed', { targetId, error: err.message });
    }
  };

  const renderMatchCard = (match) => (
    <div key={match._id} className="bg-gray-900/50 backdrop-blur-md border border-gray-800 rounded-xl overflow-hidden hover:border-red-500/50 transition-all group">
      <div className="relative h-48 overflow-hidden">
        <img 
          src={match.matchImage || "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80"} 
          alt={match.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest shadow-lg">
          {match.sport}
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-red-500 transition-colors">{match.title}</h3>
        <div className="space-y-2 mb-6">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <FiMapPin className="text-red-500" />
            <span>{match.location}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <FiCalendar className="text-red-500" />
            <span>{new Date(match.date).toLocaleDateString()} • {match.time}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <FiUsers className="text-red-500" />
            <span>{match.participants?.length || 0} / {match.maxPlayers} Players</span>
          </div>
        </div>
        <Link 
          to={`/match/${match._id}`}
          className="block w-full py-3 bg-red-500 hover:bg-red-600 text-white text-center font-bold rounded-lg transition-colors text-sm uppercase tracking-widest"
        >
          View Details
        </Link>
      </div>
    </div>
  );

  const renderPlayerCard = (player) => (
    <div key={player._id} className="bg-gray-900/50 backdrop-blur-md border border-gray-800 rounded-xl p-6 hover:border-red-500/50 transition-all flex items-center gap-4">
      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-red-500/20 flex-shrink-0">
        {player.profileImage ? (
          <img src={player.profileImage} alt={player.fullName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-500">
            <FiUser size={32} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${player._id}`} className="text-white font-bold text-lg hover:text-red-500 transition-colors block truncate">
          {player.fullName}
        </Link>
        <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold">
          {player.preferredSport || 'Multisport'} • {player.skillLevel || 'Intermediate'}
        </p>
      </div>
      {currentUser?._id !== player._id && (
        <button
          onClick={() => handleFollowToggle(player._id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold transition-all ${
            followingMap[player._id] 
              ? 'bg-gray-800 text-gray-400 hover:bg-gray-700' 
              : 'bg-red-500 text-white hover:bg-red-600'
          }`}
        >
          {followingMap[player._id] ? (
            <><FiCheck /> Following</>
          ) : (
            <><FiPlus /> Follow</>
          )}
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-black min-h-screen pb-20">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="mb-12">
          <p className="text-red-500 font-bold uppercase tracking-[0.3em] text-xs mb-2">Search Results</p>
          <h1 className="text-5xl font-extrabold text-white tracking-tight">
            Results for "<span className="text-red-500">{query}</span>"
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-12 border-b border-gray-800 pb-0 overflow-x-auto">
          {[
            { id: 'all', label: 'All Results' },
            { id: 'matches', label: 'Matches', count: matches.length },
            { id: 'players', label: 'Players', count: players.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-2 text-sm font-bold uppercase tracking-widest transition-all relative ${
                activeTab === tab.id ? 'text-red-500' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {tab.label} {tab.count !== undefined && <span className="ml-1 opacity-50">({tab.count})</span>}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 rounded-t-full shadow-[0_-4px_10px_rgba(239,68,68,0.5)]" />
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-gray-500 font-bold uppercase tracking-widest text-sm">Searching the field...</p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Matches Section */}
            {(activeTab === 'all' || activeTab === 'matches') && matches.length > 0 && (
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="text-2xl font-bold text-white uppercase tracking-wider">Matches</h2>
                  <div className="flex-1 h-[1px] bg-gray-800" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {matches.map(renderMatchCard)}
                </div>
              </div>
            )}

            {/* Players Section */}
            {(activeTab === 'all' || activeTab === 'players') && players.length > 0 && (
              <div>
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="text-2xl font-bold text-white uppercase tracking-wider">Players</h2>
                  <div className="flex-1 h-[1px] bg-gray-800" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {players.map(renderPlayerCard)}
                </div>
              </div>
            )}

            {matches.length === 0 && players.length === 0 && !loading && (
              <div className="text-center py-20 bg-gray-900/30 rounded-3xl border border-dashed border-gray-800">
                <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FiFilter className="text-gray-500 w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">No results found</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  We couldn't find any matches or players matching "{query}". Try different keywords or check your spelling.
                </p>
                <Link 
                  to="/discover" 
                  className="inline-block mt-8 text-red-500 font-bold uppercase tracking-widest text-sm hover:underline"
                >
                  Browse all matches
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
